import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import axiosInstance from '@/utils/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { UserPlus, ArrowLeft } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Register() {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [cpf, setCpf] = useState('');
  const [funcao, setFuncao] = useState('');
  const [crm, setCrm] = useState('');
  const [unitId, setUnitId] = useState('');
  const [healthUnits, setHealthUnits] = useState([]);
  const [role, setRole] = useState('medico');
  const [senha, setSenha] = useState('');
  const [confirmSenha, setConfirmSenha] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHealthUnits();
  }, []);

  const fetchHealthUnits = async () => {
    try {
      const response = await axios.get(`${API}/health-units`);
      setHealthUnits(response.data);
    } catch (error) {
      console.error('Erro ao buscar unidades:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!funcao) {
      toast.error('Selecione sua função');
      return;
    }

    if (!unitId) {
      toast.error('Selecione a unidade de saúde');
      return;
    }

    if (funcao === 'medico' && !crm) {
      toast.error('CRM é obrigatório para médicos');
      return;
    }

    if (funcao === 'dentista' && !crm) {
      toast.error('CRO é obrigatório para dentistas');
      return;
    }

    if (senha !== confirmSenha) {
      toast.error('As senhas não coincidem');
      return;
    }

    setLoading(true);

    // Determinar o role baseado na função
    let userRole = 'enfermeiro'; // padrão
    if (funcao === 'medico') userRole = 'medico';
    if (funcao === 'dentista') userRole = 'dentista';

    try {
      await axios.post(`${API}/auth/register`, {
        nome,
        email,
        cpf,
        funcao,
        crm: crm || '',
        unit_id: unitId || '',
        role: userRole,
        senha
      });

      toast.success('Cadastro realizado! Aguarde aprovação do administrador.');
      navigate('/login');
    } catch (error) {
      console.error('Erro no cadastro:', error);
      toast.error(error.response?.data?.detail || 'Erro ao cadastrar');
    } finally {
      setLoading(false);
    }
  };

  const getFuncaoLabel = (value) => {
    const labels = {
      'acs': 'ACS (Agente Comunitário de Saúde)',
      'enfermeiro': 'Enfermeiro(a)',
      'tecnico_enfermagem': 'Técnico(a) de Enfermagem',
      'medico': 'Médico(a)',
      'dentista': 'Dentista'
    };
    return labels[value] || value;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={() => navigate('/login')}
          className="flex items-center text-primary hover:underline mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para login
        </button>

        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-secondary to-accent rounded-full mx-auto mb-4 flex items-center justify-center">
            <UserPlus className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Criar Conta</h1>
          <p className="text-muted-foreground">Cadastre-se no sistema</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="nome" className="text-sm font-medium mb-2 block">
                Nome Completo *
              </Label>
              <Input
                id="nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
                className="h-12"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium mb-2 block">
                Email *
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
              />
            </div>

            <div>
              <Label htmlFor="cpf" className="text-sm font-medium mb-2 block">
                CPF *
              </Label>
              <Input
                id="cpf"
                value={cpf}
                onChange={(e) => setCpf(e.target.value)}
                placeholder="000.000.000-00"
                required
                className="h-12"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="funcao" className="text-sm font-medium mb-2 block">
                Função *
              </Label>
              <Select value={funcao} onValueChange={setFuncao} required>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Selecione sua função" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="acs">1 - ACS (Agente Comunitário de Saúde)</SelectItem>
                  <SelectItem value="enfermeiro">2 - Enfermeiro(a)</SelectItem>
                  <SelectItem value="tecnico_enfermagem">3 - Técnico(a) de Enfermagem</SelectItem>
                  <SelectItem value="medico">4 - Médico(a)</SelectItem>
                  <SelectItem value="dentista">5 - Dentista</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2">
              <Label htmlFor="unit" className="text-sm font-medium mb-2 block">
                Unidade de Saúde *
              </Label>
              <Select value={unitId} onValueChange={setUnitId} required>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Selecione a UBS onde você trabalha" />
                </SelectTrigger>
                <SelectContent>
                  {healthUnits.map((unit) => (
                    <SelectItem key={unit.id} value={unit.id}>
                      {unit.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(funcao === 'medico' || funcao === 'dentista') && (
              <div className="col-span-2">
                <Label htmlFor="crm" className="text-sm font-medium mb-2 block">
                  {funcao === 'dentista' ? 'CRO *' : 'CRM *'}
                </Label>
                <Input
                  id="crm"
                  value={crm}
                  onChange={(e) => setCrm(e.target.value)}
                  placeholder="123456"
                  required={funcao === 'medico' || funcao === 'dentista'}
                  className="h-12"
                />
              </div>
            )}

            <div>
              <Label htmlFor="senha" className="text-sm font-medium mb-2 block">
                Senha *
              </Label>
              <Input
                id="senha"
                type="password"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                className="h-12"
              />
            </div>

            <div>
              <Label htmlFor="confirmSenha" className="text-sm font-medium mb-2 block">
                Confirmar Senha *
              </Label>
              <Input
                id="confirmSenha"
                type="password"
                value={confirmSenha}
                onChange={(e) => setConfirmSenha(e.target.value)}
                required
                className="h-12"
              />
            </div>
          </div>

          {funcao && funcao !== 'medico' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Nota:</strong> Como {getFuncaoLabel(funcao)}, você poderá cadastrar receitas em nome do médico da sua unidade.
              </p>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 rounded-full text-lg font-semibold"
          >
            {loading ? 'Cadastrando...' : 'Criar Conta'}
          </Button>
        </form>
      </div>
    </div>
  );
}
